import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ArrowRight, Clock, MapPin, HelpCircle, Calculator, User } from 'lucide-react';

const WHATSAPP_NUMBER = '5531999172997';

const quickOptions = [
  { id: 'orcamento', label: 'Quero montar meu orçamento', icon: Calculator },
  { id: 'duvida', label: 'Tenho uma dúvida', icon: HelpCircle },
  { id: 'falar', label: 'Quero falar com alguém', icon: User },
  { id: 'horario', label: 'Horário de atendimento', icon: Clock },
  { id: 'local', label: 'Onde ficam vocês?', icon: MapPin },
];

const faqCategories = [
  { id: 'cardapio', label: 'Cardápio e opções', answer: 'Temos mais de 40 opções de comida de boteco, petit gourmet, pratos principais, churrasco, salgados, doces e muito mais. Você pode montar seu cardápio livremente no nosso simulador!' },
  { id: 'precos', label: 'Preços e pagamento', answer: 'Nossos valores são por pessoa e variam conforme os itens escolhidos. Aceitamos PIX, cartão e parcelamos em até 10x. Use o simulador para ver o valor na hora!' },
  { id: 'regiao', label: 'Região de atendimento', answer: 'Atendemos Belo Horizonte e toda a região metropolitana. Para eventos fora da região, consulte nosso especialista.' },
  { id: 'incluso', label: 'O que está incluso', answer: 'Equipe completa (maître, barman, garçons, cozinheira, coordenador), toda a estrutura (pratos, talheres, taças, rechaud), transporte, montagem e desmontagem. Tudo incluso!' },
  { id: 'outra', label: 'Outra dúvida', answer: null },
];

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  const [showFaqCategories, setShowFaqCategories] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [hasOpened, setHasOpened] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(scrollToBottom, [messages, isTyping]);

  const open = () => {
    setIsOpen(true);
    if (!hasOpened) {
      setHasOpened(true);
      addBotMessage('Olá! Sou o assistente da Multi Eventos. Como posso ajudar?');
    }
  };

  const addBotMessage = (text) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [...prev, { from: 'bot', text, time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }]);
    }, 600);
  };

  const addUserMessage = (text) => {
    setMessages((prev) => [...prev, { from: 'user', text, time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }]);
  };

  const handleQuickOption = (option) => {
    addUserMessage(option.label);
    setShowQuickOptions(false);

    switch (option.id) {
      case 'orcamento':
        addBotMessage('Ótimo! Vou te direcionar para nosso simulador de orçamento.');
        setTimeout(() => {
          setIsOpen(false);
          const el = document.querySelector('#simulador');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 1500);
        break;
      case 'duvida':
        addBotMessage('Sobre o que você gostaria de saber?');
        setTimeout(() => setShowFaqCategories(true), 800);
        break;
      case 'falar':
        addBotMessage('Vou te conectar com nosso especialista no WhatsApp!');
        setTimeout(() => {
          window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá! Gostaria de falar com um especialista.')}`, '_blank');
        }, 1200);
        break;
      case 'horario':
        addBotMessage('Nosso horário de atendimento é de Segunda a Sábado, das 9h às 18h.');
        setTimeout(() => setShowQuickOptions(true), 1000);
        break;
      case 'local':
        addBotMessage('Atendemos Belo Horizonte e toda a região metropolitana de MG. Para eventos fora da região, consulte nosso especialista.');
        setTimeout(() => setShowQuickOptions(true), 1000);
        break;
      default:
        break;
    }
  };

  const handleFaqCategory = (cat) => {
    addUserMessage(cat.label);
    setShowFaqCategories(false);

    if (cat.answer) {
      addBotMessage(cat.answer);
      setTimeout(() => {
        addBotMessage('Posso ajudar com mais alguma coisa?');
        setTimeout(() => setShowQuickOptions(true), 800);
      }, 1200);
    } else {
      addBotMessage('Para dúvidas específicas, nosso especialista pode te ajudar melhor!');
      setTimeout(() => {
        window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá! Tenho uma dúvida sobre o serviço de vocês.')}`, '_blank');
      }, 1500);
    }
  };

  const goBackToOptions = () => {
    setShowFaqCategories(false);
    setShowQuickOptions(true);
  };

  return (
    <>
      {/* Chat Window */}
      <div className={`fixed bottom-24 right-4 md:right-6 z-[100] w-[340px] sm:w-[360px] transition-all duration-300 ${
        isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}>
        <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/40 border border-white/10 flex flex-col" style={{ height: '480px' }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-[#8B6F47] to-[#C9A961] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="font-body text-sm text-white font-medium block leading-tight">Multi Eventos</span>
                <span className="font-body text-[10px] text-white/70 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span> Online agora
                </span>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/60 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-[#0a0a0a] px-4 py-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-lg ${
                  msg.from === 'user'
                    ? 'bg-[#8B6F47]/30 border border-[#8B6F47]/20 text-white'
                    : 'bg-[#1a1a1a] border border-white/5 text-white/80'
                }`}>
                  <p className="font-body text-sm leading-relaxed">{msg.text}</p>
                  <span className="font-body text-[9px] text-white/20 block mt-1 text-right">{msg.time}</span>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-[#1a1a1a] border border-white/5 px-4 py-3 rounded-lg">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-white/30 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-white/30 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-white/30 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Options */}
            {showQuickOptions && !isTyping && messages.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {quickOptions.map((opt) => (
                  <button key={opt.id} onClick={() => handleQuickOption(opt)}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border border-[#8B6F47]/20 hover:border-[#8B6F47]/40 hover:bg-[#8B6F47]/5 text-left transition-all">
                    <opt.icon className="w-4 h-4 text-[#C9A961] flex-shrink-0" />
                    <span className="font-body text-sm text-white/70">{opt.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* FAQ Categories */}
            {showFaqCategories && !isTyping && (
              <div className="space-y-1.5 pt-1">
                {faqCategories.map((cat) => (
                  <button key={cat.id} onClick={() => handleFaqCategory(cat)}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border border-white/10 hover:border-[#8B6F47]/30 hover:bg-[#8B6F47]/5 text-left transition-all">
                    <ArrowRight className="w-3.5 h-3.5 text-[#C9A961] flex-shrink-0" />
                    <span className="font-body text-sm text-white/70">{cat.label}</span>
                  </button>
                ))}
                <button onClick={goBackToOptions}
                  className="w-full text-center py-2 font-body text-xs text-white/30 hover:text-white/50 transition-colors">
                  Voltar ao menu
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Floating Button */}
      <button
        onClick={() => isOpen ? setIsOpen(false) : open()}
        className={`fixed bottom-8 right-4 md:right-6 z-[100] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          isOpen
            ? 'bg-[#1a1a1a] border border-white/10 shadow-black/30 rotate-0'
            : 'bg-gradient-to-r from-[#8B6F47] to-[#C9A961] shadow-[#8B6F47]/30 hover:scale-110 active:scale-95'
        }`}
        aria-label="Chat"
      >
        {isOpen
          ? <X className="w-5 h-5 text-white/60" />
          : <MessageCircle className="w-6 h-6 text-white" />
        }
      </button>
    </>
  );
};

export default Chatbot;
